/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  composeSetters,
  fieldValidationSetter,
  mergeArraysByKey,
  removeFromArrayReducer,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  updateInArrayReducer,
} from '@gh/common-shared';
import {
  complementJeCuttingListItem,
  getJeCuttingListItemId,
  isJeCuttingListItemFieldAffectsPrice,
  JeCuttingListItem,
  validateJeCuttingListItemField,
} from '../../../resources/job-estimate-details.model';
import {
  JeDetailsState,
  JeDetailsStateCuttingListItemRemoveEvent,
  JeDetailsStateCuttingListItemSetFieldEvent,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
} from '../je-details.state';

export const mergeCuttingList = mergeArraysByKey<JeCuttingListItem>('sortOrder');

const setCuttingListItemField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validateJeCuttingListItemField));

const byCuttingListItem = (event: JeDetailsStateCuttingListItemRemoveEvent | JeDetailsStateCuttingListItemSetFieldEvent,
                           item: JeCuttingListItem) => getJeCuttingListItemId(item) === event.cuttingId;
const cuttingListItemReducer = (state: JeCuttingListItem, event: JeDetailsStateCuttingListItemSetFieldEvent) =>
  setCuttingListItemField(state, event.name, event.value);

const cuttingListUpdateReducer = updateInArrayReducer(cuttingListItemReducer, byCuttingListItem);
const cuttingListRemoveReducer = removeFromArrayReducer(byCuttingListItem);

export function cuttingListReducer(cuttingList: JeCuttingListItem[],
                                   event: JeDetailsStateEvent): JeCuttingListItem[] {
  switch (event.type) {
    case JeDetailsStateEventType.CuttingListItemAdd:
      return reorderCuttingList([<JeCuttingListItem>complementJeCuttingListItem(setNewDirtyType({
        ...event.item,
        sortOrder: 0,
      }))].concat(cuttingList));
    case JeDetailsStateEventType.CuttingListItemRemove:
      return reorderCuttingList(cuttingListRemoveReducer(cuttingList, event));
    case JeDetailsStateEventType.CuttingListItemSetField:
      return cuttingListUpdateReducer(cuttingList, event);
    default:
      return cuttingList;
  }
}

export function rootCuttingListReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemRemove:
      return setRecalculationNeeded(setDirty(state));
    case JeDetailsStateEventType.CuttingListItemSetField:
      if (isJeCuttingListItemFieldAffectsPrice(event.name)) {
        return setRecalculationNeeded(setDirty(state));
      } else {
        return setDirty(state);
      }
    default:
      return state;
  }
}

function reorderCuttingList(cuttingList: JeCuttingListItem[]): JeCuttingListItem[] {
  return cuttingList.map((item, i) => setEntityField(item, 'sortOrder', i));
}
